import { useMemo } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Send, PenSquare, Receipt, DollarSign, CalendarClock, MailOpen,
  UserX, ArrowRight, Zap, Mail, Sparkles,
} from "lucide-react";

/**
 * Read-only "cheat sheet" that documents Tides Hub's built-in system triggers.
 * These are hardcoded behaviors in the backend (not user-editable workflows).
 * Users can still layer their own Stage/Event workflows on top of these.
 */
const AUTOMATIONS = [
  {
    id: "quote-sent",
    group: "Quotes",
    icon: Send,
    color: "#0F4C81",
    when: "You send a quote email",
    then: "Project auto-moves to your \"Quote Sent\" stage",
    also: "Kicks off the Universal 10-email, 60-day follow-up sequence",
    where: "Quote Builder → Send Email",
  },
  {
    id: "customer-signs",
    group: "Quotes",
    icon: PenSquare,
    color: "#059669",
    when: "Customer signs the quote",
    then: "Project auto-moves to \"Quote Approved\" (or your template's signed stage)",
    also: "Stops the follow-up drip · Fires Stage Automation for \"quote_signed_fully\"",
    where: "Public quote page → Sign & Approve",
  },
  {
    id: "customer-replies",
    group: "Quotes",
    icon: MailOpen,
    color: "#f59e0b",
    when: "Customer replies to any drip email",
    then: "The 10-email follow-up sequence auto-pauses",
    also: "Reply is logged on the project's activity feed",
    where: "Inbound webhook → /api/webhook/resend-inbound",
  },
  {
    id: "unsubscribe",
    group: "Quotes",
    icon: UserX,
    color: "#dc2626",
    when: "Customer clicks unsubscribe in a follow-up",
    then: "All future follow-ups for that customer stop",
    also: "Contact is flagged as unsubscribed",
    where: "Email footer → /api/public/unsubscribe",
  },
  {
    id: "invoice-sent",
    group: "Billing",
    icon: Receipt,
    color: "#7c3aed",
    when: "You send a deposit or final invoice",
    then: "Project auto-moves to \"Approved\" (skips if already past)",
    also: "Fires Stage Automation for \"deposit_invoice_sent\" or \"final_invoice_sent\"",
    where: "Billing tab → Send Invoice",
  },
  {
    id: "all-paid",
    group: "Billing",
    icon: DollarSign,
    color: "#059669",
    when: "Every invoice on a project is fully paid",
    then: "Project auto-moves to the \"Final Paid\" stage",
    also: "Payment receipt is emailed to the customer",
    where: "Stripe webhook or manual payment entry",
  },
  {
    id: "event-scheduled",
    group: "Schedule",
    icon: CalendarClock,
    color: "#0ea5e9",
    when: "You schedule an event (with a target stage set)",
    then: "Project auto-moves to that event type's target stage",
    also: "Common: Inspection scheduled → \"Inspection Booked\"",
    where: "Schedule → New Event · Settings → Event Types",
  },
];

const GROUP_COLORS = {
  Quotes: "#0F4C81",
  Billing: "#7c3aed",
  Schedule: "#0ea5e9",
};

export default function AutomationsMap() {
  const grouped = useMemo(() => {
    const out = {};
    for (const a of AUTOMATIONS) {
      (out[a.group] = out[a.group] || []).push(a);
    }
    return out;
  }, []);

  return (
    <div data-testid="automations-map">
      {/* Intro banner */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mb-6 flex gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-amber-600" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-800">
            Tides Hub Cheat Sheet · What happens automatically
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            These are the built-in system triggers baked into Tides Hub.
            You don&apos;t need to configure them — they just work. Any custom
            workflows you build in the tabs below fire <em>on top of</em> these.
          </p>
        </div>
      </div>

      {/* Grouped cards */}
      <div className="space-y-8">
        {Object.entries(grouped).map(([group, items]) => (
          <div key={group}>
            <div className="flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-5 rounded-full"
                style={{ backgroundColor: GROUP_COLORS[group] || "#0F4C81" }}
              />
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {group}
              </h4>
              <span className="text-[10px] text-slate-300">
                {items.length} auto-behavior{items.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((a) => {
                const Icon = a.icon;
                return (
                  <Card
                    key={a.id}
                    className="border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                    data-testid={`automation-map-card-${a.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${a.color}14` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: a.color }} />
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* WHEN */}
                          <div className="flex items-center gap-1.5 mb-2">
                            <Badge
                              className="text-[9px] px-1.5 py-0 font-semibold uppercase tracking-wide"
                              style={{
                                backgroundColor: `${a.color}14`,
                                color: a.color,
                                border: `1px solid ${a.color}25`,
                              }}
                            >
                              When
                            </Badge>
                            <p className="text-sm font-semibold text-slate-800 leading-tight">
                              {a.when}
                            </p>
                          </div>

                          {/* THEN */}
                          <div className="flex items-start gap-2 pl-1 mb-1.5">
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-slate-600 leading-relaxed">
                              <span className="font-semibold text-slate-700">Then </span>
                              {a.then}
                            </p>
                          </div>

                          {/* ALSO */}
                          {a.also && (
                            <div className="flex items-start gap-2 pl-1 mb-2">
                              <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-slate-500 leading-relaxed">
                                {a.also}
                              </p>
                            </div>
                          )}

                          {/* WHERE */}
                          {a.where && (
                            <p className="text-[10px] text-slate-300 pl-1 pt-1 border-t border-slate-100 mt-2">
                              <Mail className="w-3 h-3 inline mr-1 -mt-0.5" />
                              {a.where}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 leading-relaxed">
        <p>
          <span className="font-semibold text-slate-600">Need more control?</span> Head to
          the <strong>Stage Workflows</strong> or <strong>Event Workflows</strong> tabs
          to build your own automations that layer on top of these built-ins.
          Fine-tune where each trigger sends projects in{" "}
          <strong>Settings → Stage Automations</strong>.
        </p>
      </div>
    </div>
  );
}
